/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook used to retrieve the Theme Manager Context.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to get access to react base hooks inside custom hook.
 */
import { useEffect } from 'react';

/**
 * Allows for the dispatching of actions to the redux store.
 */
import { useDispatch } from 'react-redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Enables the use of category data loaded from the ServiceStack API.
 */
import { TermsSelectors, useTermsSelector, TermsActions } from 'src/store/reducers/terms';

/**
 * Typings
 */
import { IRequestState } from 'src/store/reducers';

/**
 * Typings
 */
import * as Dtos from '../api/dtos';

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const useTermsByCategory = (
    categoryId: number
): [
    Dtos.Version | null,
    Dtos.Category | null,
    Dtos.Term | null,
    number[],
    IRequestState
] => {
    const version = useTermsSelector(state => TermsSelectors.byCategory.selectTermsVersion(state, categoryId));
    const category = useTermsSelector(state => TermsSelectors.byCategory.selectTermsCategory(state, categoryId));
    const view = useTermsSelector(state => TermsSelectors.byCategory.selectTermsView(state, categoryId));
    const loadState = useTermsSelector(state => TermsSelectors.byCategory.selectTermsLoadState(state, categoryId));

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(TermsActions.loadByCategoryId(categoryId));

        return () => {
            dispatch(TermsActions.clear('byCategory', categoryId));
        }
    }, [categoryId, dispatch]);

    return [
        version,
        category,
        null,
        view,
        loadState
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Exports - Default
 * ---------------------------------------------------------------------------------
 */

export default useTermsByCategory;


// WEBPACK FOOTER //
// ./src/hooks/useTermsByCategory.ts